/* eslint-disable import/prefer-default-export */
import { appSettings } from 'bam-node-baseball-savant/leaderboard/appSettings'
import { CompToolPlayer } from './types.ts'

const INFIELD_TYPES = [3, 4, 5, 6]
const OUTFIELD_TYPES = [7, 8, 9]
const PITCHER_TYPE = 1
const CATCHER_TYPE = 2

function getDiffYears(
    allPlayers: CompToolPlayer[],
    firstPlayer: CompToolPlayer
) {
    const { currentYear } = appSettings.SETTINGS_COMPARISON_TOOL
    const diffYearArr: CompToolPlayer[] = []

    const playerDiffYears = allPlayers
        .filter(
            (player) =>
                Number(player.id) === Number(firstPlayer.id) &&
                Number(player.year) !== Number(firstPlayer.year) &&
                player.grouping_code === firstPlayer.grouping_code
        )
        .sort((a, b) => b.year - a.year)

    if (playerDiffYears.length === 0) {
        return diffYearArr
    }

    const otherYears = playerDiffYears.map((d) => d.year)
    const firstYear = Math.min(...otherYears)

    if (firstPlayer.year - firstYear <= 3) {
        return playerDiffYears.slice(-3)
    }

    if (currentYear - firstPlayer.year <= 3) {
        return playerDiffYears.slice(0, 3)
    }

    if (otherYears.includes(currentYear)) {
        const currYear = playerDiffYears.find((d) => d.year === currentYear)
        if (currYear) {
            diffYearArr.push(currYear)
        }
    }

    if (firstPlayer.year + 1 !== currentYear) {
        const nextYear = playerDiffYears.find(
            (d) => d.year === firstPlayer.year + 1 && d.year !== currentYear
        )
        if (nextYear) {
            diffYearArr.push(nextYear)
        }
    } else {
        const twoYears = playerDiffYears.find(
            (d) => d.year === firstPlayer.year + 2 && d.year !== currentYear
        )
        if (twoYears) {
            diffYearArr.push(twoYears)
        }
    }

    if (firstPlayer.year - 1 !== currentYear) {
        const nextYear = playerDiffYears.find(
            (d) => d.year === firstPlayer.year - 1
        )
        if (nextYear) {
            diffYearArr.push(nextYear)
        }
    } else {
        const twoYears = playerDiffYears.find(
            (d) => d.year === firstPlayer.year - 2
        )
        if (twoYears) {
            diffYearArr.push(twoYears)
        }
    }

    return diffYearArr
}

function getTeammates(
    allPlayers: CompToolPlayer[],
    firstPlayer: CompToolPlayer
) {
    if (!firstPlayer.teams || firstPlayer.teams.length === 0) {
        return []
    }

    const latestTeam = firstPlayer.teams[firstPlayer.teams.length - 1]
    const searchYear = firstPlayer.year

    const teammates = allPlayers.filter((d) => {
        const teamArr = d.teams
        if (!teamArr || teamArr.length === 0) {
            return false
        }

        return (
            teamArr[teamArr.length - 1] === latestTeam &&
            d.year === searchYear &&
            d.uniqueId !== firstPlayer.uniqueId &&
            d.grouping_code === firstPlayer.grouping_code
        )
    })

    let filteredTeammates: CompToolPlayer[]

    if (Number(firstPlayer.pos) === PITCHER_TYPE) {
        filteredTeammates = teammates.filter(
            (player) => Number(player.pos) === PITCHER_TYPE
        )
    } else if (Number(firstPlayer.pos) === CATCHER_TYPE) {
        filteredTeammates = teammates.filter(
            (player) => Number(player.pos) === CATCHER_TYPE
        )
    } else if (INFIELD_TYPES.includes(Number(firstPlayer.pos))) {
        filteredTeammates = teammates.filter((player) =>
            INFIELD_TYPES.includes(Number(player.pos))
        )
    } else if (OUTFIELD_TYPES.includes(Number(firstPlayer.pos))) {
        filteredTeammates = teammates.filter((player) =>
            OUTFIELD_TYPES.includes(Number(player.pos))
        )
    } else {
        filteredTeammates = []
    }

    return filteredTeammates
        .sort((a, b) => Number(b.pos_opps) - Number(a.pos_opps))
        .slice(0, 4)
}

function getPositionMates(
    allPlayers: CompToolPlayer[],
    firstPlayer: CompToolPlayer
) {
    if (!firstPlayer.pos) {
        return []
    }

    const positionGuys = allPlayers.filter(
        (player) =>
            player.pos === firstPlayer.pos &&
            player.year === firstPlayer.year &&
            player.uniqueId !== firstPlayer.uniqueId &&
            player.grouping_code === firstPlayer.grouping_code
    )

    return positionGuys
        .sort((a, b) => Number(b.pos_opps) - Number(a.pos_opps))
        .slice(0, 3)
}

export function getSimilarPlayers(
    allPlayers: CompToolPlayer[],
    firstPlayer: CompToolPlayer
): CompToolPlayer[] {
    const playerDiffYears = getDiffYears(allPlayers, firstPlayer)
    const teammates = getTeammates(allPlayers, firstPlayer)
    const positionMates = getPositionMates(allPlayers, firstPlayer)

    return [...new Set([...playerDiffYears, ...teammates, ...positionMates])]
}
